import { useAppConfig } from 'application/configuration'
import { applicationStarted } from 'application/model'
import { logger } from 'lib/logger'
import React, { FC, Suspense, useEffect } from 'react'
import { hot } from 'react-hot-loader/root'
import { SSRStages, stages as allStages } from 'stages/stages'
import styled, { useTheme } from 'styled-components'
import { HeadingByTag } from 'ui/heading'

import { getProjectName } from '../lib/get-project-name'
import { ReactComponent as StaticLoader } from './staticLoader.svg'

export const AppComponent: FC = () => {
  const config = useAppConfig()

  useEffect(() => {
    applicationStarted(config)
  }, [config])

  const stages = config.stages.filter((stage) => {
    if (__SSR__ && !(stage?.id in SSRStages)) return false
    return stage?.id in allStages && stage?.active
  })

  const stagesView = stages.map((stage) => {
    const Component = __SSR__ ? SSRStages[stage?.id] : allStages[stage?.id]

    logger.log(`[App/StageRender] ${stage?.id}`, stage?.config, config)
    return (
      <Stage key={stage?.id}>
        {stage?.title && (
          <HeadingByTag tag={stage?.title?.tag} semantic={stage?.title?.semantic}>
            {stage?.title?.value}
          </HeadingByTag>
        )}
        {__SSR__ ? (
          /*@ts-ignore*/
          <Component {...stage?.config} />
        ) : (
          <Suspense fallback={<StaticPreloader />}>
            {/*@ts-ignore*/}
            <Component {...stage?.config} />
          </Suspense>
        )}
      </Stage>
    )
  })

  if (__SSR__)
    return (
      <StaticPreloader>
        {/*
          Скрипт инъектирует переменную в глобал, чтобы смарт-контракт мог понять, что нужно запускаться не через пресет,
          а ожидать от нас запроса на инициализацию. Вторая переменная нужна для работы logger
          */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
                window['@kodix/isEcomEnzo'] = true;
                if(!window['@kodix/ecomEnzoRenderTime']) window['@kodix/ecomEnzoRenderTime'] = []
                window['@kodix/ecomEnzoRenderTime'].push({
                    stages:'${config.stages.map((stage) => stage.id).join(',')}',
                    time: ${Date.now()}
                })
            `
          }}
        />
        {stagesView}
      </StaticPreloader>
    )

  return <>{stagesView}</>
}

const StaticPreloader: FC = ({ children }) => {
  const theme = useTheme()
  const themeName = getProjectName()

  let bg = 'gray'
  if (themeName && themeName === 'generic_v2') {
    bg = `${theme.palette.grey[800]}`
  } else {
    bg = `${theme.palette.grey[50]}`
  }
  return (
    <div
      style={{
        position: `relative`,
        width: `100%`,
        height: `400px`,
        overflow: `hidden`
      }}
    >
      {children}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          color: theme.palette.brand.primary || 'darkgray'
        }}
      >
        <StaticLoader style={{ width: '64px', height: '64px' }} />
      </div>
    </div>
  )
}

export const App = hot(AppComponent)

const Stage = styled.div`
  padding: ${({ theme }) => `${theme.spacing[0]} ${theme.spacing[0]}`};
  line-height: normal;
`
